import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PersonIcon,
  AvatarIcon,
  ClockIcon,
  FileTextIcon,
  IdCardIcon,
  CalendarIcon,
  ReaderIcon,
  TimerIcon,
  CameraIcon,
  ActivityLogIcon,
  BarChartIcon,
  LightningBoltIcon,
  UpdateIcon,
  ChevronRightIcon,
  StopwatchIcon,
  CheckCircledIcon,
  LayersIcon,
  ExclamationTriangleIcon,
  SewingPinIcon,
  ClipboardIcon,
  RulerHorizontalIcon,
} from '@radix-ui/react-icons';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Global.css';
import './AdminDashboard.css';

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

const recordDateKey = (record) => String(record?.date || '').split('T')[0];

const formatDateBR = (isoDate) => {
  if (!isoDate) return '-';
  const [year, month, day] = isoDate.split('-');
  if (!day) return isoDate;
  return `${day}/${month}/${year}`;
};

const formatTime = (time) => (time ? String(time).substring(0, 5) : null);

const getInitials = (name) => {
  if (!name) return '--';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '--';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const SHORTCUTS = [
  { to: '/admin/users', label: 'Militares', desc: 'Cadastro e edição do efetivo', icon: <PersonIcon />, accent: 'ad-accent-green' },
  { to: '/admin/civilians', label: 'Civis', desc: 'Colaboradores civis do CMCB', icon: <AvatarIcon />, accent: 'ad-accent-violet' },
  { to: '/admin/time-records', label: 'Registros', desc: 'Entradas, saídas e ajustes', icon: <ClockIcon />, accent: 'ad-accent-blue' },
  { to: '/admin/lancamento-massa', label: 'Lançamento em massa', desc: 'Fecha o ciclo do efetivo do CMCB', icon: <LayersIcon />, accent: 'ad-accent-green' },
  { to: '/admin/escala', label: 'Escala de itinerantes', desc: 'Vagas por turno, feriados e aprovações', icon: <ClipboardIcon />, accent: 'ad-accent-amber' },
  { to: '/admin/reports', label: 'Relatórios', desc: 'Exportação de horas-aula', icon: <FileTextIcon />, accent: 'ad-accent-green' },
  { to: '/admin/positions', label: 'Funções', desc: 'Funções e cargas horárias', icon: <IdCardIcon />, accent: 'ad-accent-blue' },
  { to: '/admin/licenses', label: 'Licenças', desc: 'Afastamentos e impedimentos', icon: <CalendarIcon />, accent: 'ad-accent-amber' },
  { to: '/admin/audit', label: 'Auditoria', desc: 'Histórico de alterações', icon: <ReaderIcon />, accent: 'ad-accent-violet' },
  { to: '/admin/auto-close', label: 'Fechamento', desc: 'Encerramento automático', icon: <TimerIcon />, accent: 'ad-accent-amber' },
  { to: '/admin/face', label: 'Facial', desc: 'Verificação por reconhecimento', icon: <CameraIcon />, accent: 'ad-accent-green' },
  { to: '/admin/school-location', label: 'Localização', desc: 'Referência e raio do ponto pelo app', icon: <SewingPinIcon />, accent: 'ad-accent-blue' },
  { to: '/admin/limites', label: 'Limites de horas', desc: 'Tetos por ciclo e excedentes retidos', icon: <RulerHorizontalIcon />, accent: 'ad-accent-amber' },
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRecords: 0,
    recentActivity: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allRecords, setAllRecords] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(10);
  const [now, setNow] = useState(() => new Date());

  const formatDecimalToHours = (decimal) => {
    if (!decimal) return '0h';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}h${minutes.toString().padStart(2, '0')}min`;
  };

  const loadDashboardData = useCallback(async (limit) => {
    try {
      const [usersRes, recordsRes] = await Promise.all([
        api.get('/users'),
        api.get('/time-records'),
      ]);

      const records = Array.isArray(recordsRes.data) ? recordsRes.data : recordsRes.data.data || [];
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      setAllRecords(records);
      setAllUsers(users);

      setStats({
        totalUsers: users.length,
        totalRecords: records.length,
        recentActivity: records.slice(0, limit),
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData(10);
  }, [loadDashboardData]);

  // Relógio do cabeçalho
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const loadMore = () => {
    const newLimit = displayLimit + 10;
    setDisplayLimit(newLimit);
    setStats(prevStats => ({
      ...prevStats,
      recentActivity: allRecords.slice(0, newLimit),
    }));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData(displayLimit);
    setRefreshing(false);
  };

  const hasMore = displayLimit < allRecords.length;

  const todayKey = toDateKey(now);

  const metrics = useMemo(() => {
    const militares = allUsers.filter((u) => u.personnelType !== 'civil').length;
    const civis = allUsers.filter((u) => u.personnelType === 'civil').length;

    const recordsToday = allRecords.filter((r) => recordDateKey(r) === todayKey);
    const openToday = recordsToday.filter((r) => !r.exitTime).length;

    /* Entrada sem saída de dia ANTERIOR não é "em serviço": é ponto que o
       fiscal esqueceu de fechar e que não paga hora nenhuma enquanto ficar
       assim. Some aqui para o painel apontar o problema em vez de escondê-lo
       dentro do contador de hoje. */
    const openBacklog = allRecords.filter(
      (r) => !r.exitTime && recordDateKey(r) < todayKey,
    ).length;

    const monthPrefix = todayKey.substring(0, 7);
    const monthRecords = allRecords.filter((r) => recordDateKey(r).startsWith(monthPrefix));
    const monthHours = monthRecords.reduce((sum, r) => sum + (Number(r.totalHours) || 0), 0);

    return {
      militares,
      civis,
      recordsToday: recordsToday.length,
      openToday,
      openBacklog,
      monthRecords: monthRecords.length,
      monthHours,
    };
  }, [allUsers, allRecords, todayKey]);

  // Registros por dia nos últimos 7 dias
  const weekChart = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const key = toDateKey(day);
      days.push({
        key,
        label: WEEKDAYS_SHORT[day.getDay()],
        isToday: key === todayKey,
        count: allRecords.filter((r) => recordDateKey(r) === key).length,
      });
    }
    const max = Math.max(...days.map((d) => d.count), 1);
    return { days, max };
  }, [allRecords, now, todayKey]);

  const firstName = user?.name ? String(user.name).trim().split(/\s+/)[0] : '';
  const rawToday = now.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const todayLabel = rawToday.charAt(0).toUpperCase() + rawToday.slice(1);
  const clockLabel = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="ad-page">
          <div className="ad-skel ad-skel-hero" />
          <div className="ad-kpis">
            <div className="ad-skel ad-skel-kpi" />
            <div className="ad-skel ad-skel-kpi" />
            <div className="ad-skel ad-skel-kpi" />
            <div className="ad-skel ad-skel-kpi" />
          </div>
          <div className="ad-grid">
            <div className="ad-col">
              <div className="ad-skel ad-skel-card" />
              <div className="ad-skel ad-skel-card-tall" />
            </div>
            <div className="ad-col">
              <div className="ad-skel ad-skel-card-tall" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="ad-page">
        {/* Cabeçalho */}
        <header className="ad-hero">
          <div className="ad-hero-text">
            <span className="ad-hero-eyebrow">
              <LightningBoltIcon width={12} height={12} />
              Painel administrativo
            </span>
            <h1 className="ad-hero-title">
              {firstName ? `Olá, ${firstName}` : 'Dashboard Administrativo'}
            </h1>
            <p className="ad-hero-sub">{todayLabel}</p>
          </div>

          <div className="ad-hero-side">
            <div className="ad-hero-clock">
              <span className="ad-hero-clock-time">{clockLabel}</span>
              <span className="ad-hero-clock-label">Agora</span>
            </div>
            <button
              type="button"
              className={`ad-refresh ${refreshing ? 'is-loading' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
              title="Atualizar dados"
            >
              <UpdateIcon width={15} height={15} />
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>
        </header>

        {/* Indicadores */}
        <section className="ad-kpis">
          <article className="ad-kpi ad-accent-green">
            <div className="ad-kpi-top">
              <h3 className="ad-kpi-label">Total de Usuários</h3>
              <span className="ad-kpi-icon"><PersonIcon width={19} height={19} /></span>
            </div>
            <p className="ad-kpi-value">{stats.totalUsers}</p>
            <div className="ad-kpi-foot">
              <span className="ad-kpi-chip is-militar">
                <span className="ad-kpi-chip-dot" />
                {metrics.militares} militares
              </span>
              <span className="ad-kpi-chip is-civil">
                <span className="ad-kpi-chip-dot" />
                {metrics.civis} civis
              </span>
            </div>
          </article>

          <article className="ad-kpi ad-accent-blue">
            <div className="ad-kpi-top">
              <h3 className="ad-kpi-label">Total de Registros</h3>
              <span className="ad-kpi-icon"><ClockIcon width={19} height={19} /></span>
            </div>
            <p className="ad-kpi-value">{stats.totalRecords}</p>
            <p className="ad-kpi-foot">
              {metrics.monthRecords} neste mês &middot; {formatDecimalToHours(metrics.monthHours)}
            </p>
          </article>

          <article className="ad-kpi ad-accent-violet">
            <div className="ad-kpi-top">
              <h3 className="ad-kpi-label">Registros de Hoje</h3>
              <span className="ad-kpi-icon"><CalendarIcon width={19} height={19} /></span>
            </div>
            <p className="ad-kpi-value">{metrics.recordsToday}</p>
            <p className="ad-kpi-foot">{formatDateBR(todayKey)}</p>
          </article>

          <article className="ad-kpi ad-accent-amber">
            <div className="ad-kpi-top">
              <h3 className="ad-kpi-label">Em Serviço Agora</h3>
              <span className="ad-kpi-icon"><StopwatchIcon width={19} height={19} /></span>
            </div>
            <p className="ad-kpi-value">{metrics.openToday}</p>
            <p className="ad-kpi-foot">
              {metrics.openToday === 0
                ? 'Nenhuma entrada em aberto hoje'
                : `${metrics.openToday === 1 ? 'Entrada em aberto' : 'Entradas em aberto'} sem saída`}
            </p>
            {metrics.openBacklog > 0 && (
              <Link to="/admin/time-records?status=aberto" className="ad-kpi-alert">
                <ExclamationTriangleIcon width={13} height={13} />
                {metrics.openBacklog === 1
                  ? '1 entrada de dias anteriores sem saída'
                  : `${metrics.openBacklog} entradas de dias anteriores sem saída`}
                <ChevronRightIcon width={13} height={13} />
              </Link>
            )}
          </article>
        </section>

        <div className="ad-grid">
          <div className="ad-col">
            {/* Gráfico dos últimos 7 dias */}
            <section className="ad-card">
              <div className="ad-card-head">
                <div className="ad-card-head-main">
                  <span className="ad-card-head-icon"><BarChartIcon width={17} height={17} /></span>
                  <div>
                    <h2 className="ad-card-title">Últimos 7 dias</h2>
                    <p className="ad-card-sub">Registros de ponto por dia</p>
                  </div>
                </div>
                <span className="ad-card-badge">
                  {weekChart.days.reduce((sum, d) => sum + d.count, 0)} no período
                </span>
              </div>
              <div className="ad-card-body">
                <div className="ad-chart">
                  {weekChart.days.map((day) => (
                    <div
                      key={day.key}
                      className={`ad-chart-col ${day.isToday ? 'is-today' : ''}`}
                      title={`${formatDateBR(day.key)}: ${day.count} registro(s)`}
                    >
                      <span className="ad-chart-count">{day.count}</span>
                      <div className="ad-chart-track">
                        <div
                          className={`ad-chart-bar ${day.count === 0 ? 'is-zero' : ''}`}
                          style={{ height: `${Math.max((day.count / weekChart.max) * 100, 4)}%` }}
                        />
                      </div>
                      <span className="ad-chart-label">{day.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Atividade recente */}
            <section className="ad-card">
              <div className="ad-card-head">
                <div className="ad-card-head-main">
                  <span className="ad-card-head-icon"><ActivityLogIcon width={17} height={17} /></span>
                  <div>
                    <h2 className="ad-card-title">Atividade Recente</h2>
                    <p className="ad-card-sub">Últimos registros lançados no sistema</p>
                  </div>
                </div>
                <Link to="/admin/time-records" className="ad-btn-more">
                  Ver todos
                  <ChevronRightIcon width={15} height={15} />
                </Link>
              </div>

              {stats.recentActivity.length === 0 ? (
                <div className="ad-empty">
                  <span className="ad-empty-icon">
                    <ActivityLogIcon width={26} height={26} />
                  </span>
                  <p className="ad-empty-title">Nenhuma atividade recente</p>
                  <p className="ad-empty-sub">Os registros de ponto aparecerão aqui assim que forem lançados.</p>
                </div>
              ) : (
                <>
                  <div className="ad-card-body-flush">
                    <div className="ad-activity">
                      {stats.recentActivity.map((record) => {
                        const isCivil = record.user?.personnelType === 'civil';
                        const isOpen = !record.exitTime;
                        const entry = formatTime(record.entryTime);
                        const exit = formatTime(record.exitTime);
                        return (
                          <div key={record.id} className="ad-act-item">
                            <span className={`ad-act-avatar ${isCivil ? 'is-civil' : ''}`}>
                              {getInitials(record.user?.name)}
                            </span>

                            <div className="ad-act-main">
                              <span className="ad-act-name">{record.user?.name || 'Usuário removido'}</span>
                              <span className="ad-act-meta">
                                <span className="ad-act-meta-item">
                                  <CalendarIcon width={12} height={12} />
                                  {formatDateBR(recordDateKey(record))}
                                </span>
                                <span className="ad-act-meta-sep" />
                                <span className="ad-act-meta-item">
                                  <ClockIcon width={12} height={12} />
                                  {entry || '--:--'}
                                  {exit ? ` às ${exit}` : ''}
                                </span>
                                {record.position?.name && (
                                  <>
                                    <span className="ad-act-meta-sep" />
                                    <span className="ad-act-meta-item">
                                      <IdCardIcon width={12} height={12} />
                                      {record.position.name}
                                    </span>
                                  </>
                                )}
                              </span>
                            </div>

                            <div className="ad-act-right">
                              <span className="ad-act-hours">
                                {formatDecimalToHours(record.totalHours)}
                              </span>
                              <span className={`ad-status ${isOpen ? 'is-open' : 'is-done'}`}>
                                <span className="ad-status-dot" />
                                {isOpen ? 'Em serviço' : 'Concluído'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="ad-list-foot">
                    <span className="ad-list-count">
                      Mostrando <strong>{stats.recentActivity.length}</strong> de{' '}
                      <strong>{allRecords.length}</strong> registros
                    </span>
                    {hasMore && (
                      <button type="button" className="ad-btn-more" onClick={loadMore}>
                        Carregar mais
                        <ChevronRightIcon width={15} height={15} />
                      </button>
                    )}
                    {!hasMore && (
                      <span className="ad-status is-done">
                        <CheckCircledIcon width={12} height={12} />
                        Tudo carregado
                      </span>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>

          {/* Acesso rápido */}
          <aside className="ad-col ad-col-side">
            <section className="ad-card">
              <div className="ad-card-head">
                <div className="ad-card-head-main">
                  <span className="ad-card-head-icon"><LightningBoltIcon width={17} height={17} /></span>
                  <div>
                    <h2 className="ad-card-title">Acesso Rápido</h2>
                    <p className="ad-card-sub">Módulos administrativos</p>
                  </div>
                </div>
              </div>
              <div className="ad-card-body-flush">
                <nav className="ad-shortcuts">
                  {SHORTCUTS.map((item) => (
                    <Link key={item.to} to={item.to} className={`ad-shortcut ${item.accent}`}>
                      <span className="ad-shortcut-icon">{item.icon}</span>
                      <span className="ad-shortcut-text">
                        <span className="ad-shortcut-label">{item.label}</span>
                        <span className="ad-shortcut-desc">{item.desc}</span>
                      </span>
                      <ChevronRightIcon className="ad-shortcut-arrow" width={16} height={16} />
                    </Link>
                  ))}
                </nav>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
